import { useEffect, useImperativeHandle, useRef, useState } from "react";
import axios from "axios";
import { createPortal } from "react-dom"
import { DropdownContext, useDropdownContext } from "./Context";
// context
const getRandomId = (l = 32) => {
	const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
	return Array.from({ length: l }, () =>
		characters.charAt(Math.floor(Math.random() * characters.length))
	).join('');
}
const API_URL = window.API_URL;
const GET_DATA = async () => {
	const response = await axios.get(`${API_URL}list/cat-sub-rec`, {
		headers: window.HEADER_OBJ
	});
	let data = response.data
	data = data.map(category => {
		return {
			name: category.name,
			value: category.id,
			active: category.is_active,
			children: category.subcategories?.map(subcategory => {
				return {
					name: subcategory.name,
					value: subcategory.id,
					active: subcategory.is_active,
					children: subcategory.recordTypes?.map(recordtype => {
						return {
							active: recordtype.is_active,
							name: recordtype.name,
							value: recordtype.id,
						}
					})
				}
			})
		}
	})
	return data;
}


function preprocess(data, layerIndex = 0) {
	return data.map(item => {
		const newItem = {
			...item,
			id: getRandomId(),
			isOpen: false,
			layerIndex: layerIndex,
		};

		if (Array.isArray(newItem.children)) {
			newItem.children = preprocess(newItem.children, (layerIndex + 1)); // Recursive call
		}
		return newItem;
	});
}

function flattenData(data) {
	let items = [];
	data.forEach(item => {
		items.push(item);
		if (item.children) {
			items = items.concat(flattenData(item.children));
		}
	})
	return items;
}

function MultiLayerDropdown({ id, name }) {
	const [items, setItems] = useState([]); // the display data for the component

	const [globalItems, setGlobalItems] = useState([]); // the global UNCHANGING data for the component
	const [globalItemMap, setGlobalItemMap] = useState(new Map()); // the global data for the component

	const [selectedItemsMap, setSelectedItemsMap] = useState(new Map());

	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	const [layers, setLayers] = useState([{
		name: 'level 1',
		key: 'key1'
	}, {
		name: 'level 2',
		key: 'key2'
	}, {
		name: 'level 3',
		key: 'key3'
	}, {
		name: 'level 4',
		key: 'key4'
	}])

	const wrapperRef = useRef(null)
	const dropdownRef = useRef(null)
	const inputRef = useRef(null)

	const [focusedElement, setFocusedElement] = useState(null);
	useEffect(() => {
		if (focusedElement) {
			focusedElement.focus()
		}
	}, [focusedElement])

	useEffect(() => {
		setItems(globalItems ?? [])
		setGlobalItemMap(new Map(flattenData(globalItems).map(item => [item.id, item])))
	}, [globalItems])

	useEffect(() => {
		GET_DATA().then((data) => {
			const d = preprocess(data)
			setGlobalItems(d)
		})

		const openDropdown = (e) => {
			if (e.target.closest(`.multi-layer-dropdown-main-wrapper[data-id=${id}] .multi-layer-dropdown-input`) || e.target.closest(`.multi-layer-dropdown-wrapper[data-id=${id}]`) || e.target.closest(`.multi-layer-dropdown-chip-container-wrapper[data-id=${id}]`)) {
				setIsDropdownOpen(true);
			} else {
				setIsDropdownOpen(false);
			}
		}

		// select item 1 after 500ms

		document.addEventListener('click', openDropdown);

		return () => {
			document.removeEventListener('click', openDropdown);
		}

	}, [])

	const onKeyDown = (e) => {
		const keysToCheck = ['ArrowUp', 'ArrowDown', 'ArrowRight', 'ArrowLeft', 'Enter']

		if (focusedElement === inputRef.current) {
			keysToCheck.pop()
		}

		if (!keysToCheck.includes(e.key)) {
			setFocusedElement(inputRef.current)
			return;
		}

		e.stopPropagation();
		e.preventDefault();
		// up down keys
		const upDown = ['ArrowUp', 'ArrowDown']
		const leftRight = ['ArrowLeft', 'ArrowRight']

		if (upDown.includes(e.key)) {
			// use doc
			if (!focusedElement) {
				setFocusedElement(dropdownRef.current.querySelector('.group .focusable-item'))
			}

			const items = dropdownRef.current.querySelectorAll('.group .focusable-item')
			const index = Array.from(items).indexOf(focusedElement)

			if (e.key === 'ArrowDown') {
				if (index < items.length - 1) {
					setFocusedElement(items[index + 1])
				}
			} else if (e.key === 'ArrowUp') {
				if (index > 0) {
					setFocusedElement(items[index - 1])
				} else {
					setFocusedElement(inputRef.current)
				}
			}
		}

		if (leftRight.includes(e.key)) {
			if (e.key === 'ArrowRight') {
				// open 
				if (focusedElement && focusedElement.hasChildren) {
					focusedElement.setIsInnerGroupOpen(true)
				}
			} else {
				// close
				if (focusedElement && focusedElement.hasChildren && focusedElement.isInnerGroupOpen) {
					focusedElement.setIsInnerGroupOpen(false)
				}
			}
		}

		if (e.key === 'Enter') {
			if (focusedElement) {
				focusedElement.querySelector('input').click()
			}
		}
	}


	const search = (e) => {
		const value = e.target.value.toLowerCase();

		if (!value) {
			setTimeout(() => {
				setItems(JSON.parse(JSON.stringify(globalItems)));
			}, 10)
		}

		const itemsToSearch = JSON.parse(JSON.stringify(globalItems)); // Deep clone the global items

		const searchItems = (items) => {
			return items.filter((item) => {
				let isMatch = item.name.toLowerCase().includes(value); // Check if the current item matches

				if (item.children) {
					// Recursively search in children
					item.children = searchItems(item.children); // Filter children
					if (item.children.length > 0) {
						isMatch = true; // If any child matches, this parent should also match
						item.isOpen = true; // Set `isOpen` to true for parents of matching children
					} else {
						item.isOpen = false; // Ensure `isOpen` is reset if no children match
					}
				}

				return isMatch; // Include the item if it matches or any child matches
			});
		};

		const filteredItems = searchItems(itemsToSearch);
		setItems(filteredItems); // Update the filtered view
	};
	return (
		<DropdownContext.Provider value={{ items, isDropdownOpen, setIsDropdownOpen, setItems, wrapperRef, id, selectedItemsMap, setSelectedItemsMap, layers, globalItemMap, name }} >
			<div className="multi-layer-dropdown-main-wrapper" ref={wrapperRef} data-id={id} onKeyDown={onKeyDown}>
				<Input placeholder="Search" onChange={search} ref={inputRef} />
				<ChipContainer />
				<FormInputContainer>
					<InputSingleNameMergedValues />
				</FormInputContainer>
				{
					// react portal 
					isDropdownOpen &&
					createPortal(
						<Dropdown ref={dropdownRef} />,
						document.body
					)
				}
			</div>
		</DropdownContext.Provider>
	)
}

const FormInputContainer = ({ children }) => {
	return (
		<div className="form-input-container">
			{children}
		</div>
	)
}


/* gives input fields like this
	<input type="hidden" name="key1" value="1" />
	<input type="hidden" name="key1" value="2" />
	<input type="hidden" name="key2" value="1" />
	...
*/
const InputIndividualKeysIndividualValues = () => {
	const { selectedItemsMap, layers } = useDropdownContext();

	return (
		Array.from(selectedItemsMap).map(([key, item], index) => <input key={index} type="hidden" name={`${layers[item.layerIndex].key}`} value={item.value} />)
	)
}

/* gives input fields like this
	<input type="hidden" name="key1" value="[1,3,5]" />
	<input type="hidden" name="key2" value="[2,3,5]" />
	...
*/
const InputIndividualKeysMergedValues = () => {
	const { selectedItemsMap, layers } = useDropdownContext();

	const mergedValues = new Map();

	Array.from(selectedItemsMap).forEach(([key, item]) => {
		if (!mergedValues.has(item.layerIndex)) {
			mergedValues.set(item.layerIndex, [])
		}
		mergedValues.get(item.layerIndex).push(item.value)
	})

	return (
		Array.from(mergedValues).map(([layerIndex, values], index) => <input key={index} type="hidden" name={`${layers[layerIndex].key}`} value={JSON.stringify(values)} />)
	)

}

/* gives input fields like this
	<input type="hidden" name="myname" value="{"key1":[1,2],"key2":[22,23,24]}">
*/
const InputSingleNameMergedValues = () => {
	const { selectedItemsMap, layers, name } = useDropdownContext();

	if (!name) {
		throw new Error('Name prop is required for InputSingleNameMergedValues component');
	}

	const mergedValues = layers.reduce((acc, layer, index) => {
		acc[layer.key] = [];
		return acc;
	}, {});

	Array.from(selectedItemsMap).forEach(([key, item]) => {
		mergedValues[layers[item.layerIndex].key].push(item.value);
	});

	// get rid of empty arrays
	Object.keys(mergedValues).forEach(key => {
		if (mergedValues[key].length === 0) {
			delete mergedValues[key]
		}
	})

	return (
		<input
			type="hidden"
			name={name}
			value={JSON.stringify(mergedValues)}
		/>
	);
};


const _Dropdown = ({ }, ref) => {
	const { items, id, wrapperRef, selectedItemsMap } = useDropdownContext();
	if (!ref)
		ref = useRef(null)

	// css 
	useEffect(() => {
		// calculate css
		const calculatePosition = (element, dropdown, bias = 5) => {
			// set position
			const element_rect = element.getBoundingClientRect()

			// this is working if document is scrolled
			dropdown.style.top = `${element_rect.top + element_rect.height + bias + window.scrollY}px`
			dropdown.style.left = `${element_rect.left + window.scrollX}px`

			// set width
			dropdown.style.width = `${element_rect.width}px`

			// check if dropdown is overflowing vertically
			const dropdown_rect = dropdown.getBoundingClientRect()

			if (dropdown_rect.bottom > window.innerHeight) {
				dropdown.style.top = `${element_rect.top - dropdown_rect.height - bias + window.scrollY}px`
			}
		}

		if (wrapperRef.current && ref.current) {
			calculatePosition(wrapperRef.current, ref.current)
		}
	}, [wrapperRef, ref, selectedItemsMap])

	return (
		<div className={`multi-layer-dropdown-wrapper`} ref={ref} data-id={id} tabIndex={0}>
			<div className="multi-layer-dropdown">
				<Group items={items} layerIndex={0} />
			</div>
		</div>
	)
}

const Dropdown = React.forwardRef(_Dropdown)


const _Group = ({ items, layerIndex }, ref) => {

	const { setSelectedItemsMap, layers } = useDropdownContext();
	const name = layers[layerIndex].name;
	// array of refs
	const refs = useRef(items.map(() => React.createRef()));

	const toggleItemSelection = (e, item) => {
		setSelectedItemsMap(prev => {
			const newSelectedItems = new Map(prev);

			if (item.active && e.target.checked) {
				if (!newSelectedItems.has(item.id)) {
					newSelectedItems.set(item.id, item);
					return newSelectedItems; // Update state only if necessary
				}
			} else {
				e.target.checked = false;
				if (newSelectedItems.has(item.id)) {
					newSelectedItems.delete(item.id);
					return newSelectedItems; // Update state only if necessary
				}
			}

			return prev; // No change; return the previous state
		});
	};


	useEffect(() => {
		if (ref && ref.current) {
			ref.current.items = refs.current.map(ref => ref.current)
		}
	}, [ref, refs])

	return (
		<div className="group" ref={ref ? ref : null}>
			<GroupLabel title={name} items={items} />
			{
				items.map((item, index) => <DropdownItemParentSelectAll ref={
					refs.current[index]
				} key={index} item={item} layerIndex={layerIndex} onChange={toggleItemSelection} />)
			}
		</div>
	)
}


const allItemsContainedInMap = (items, map) => {
	return items.every(item => map.has(item.id))
}


const Group = React.forwardRef(_Group)

const GroupLabel = ({ title, items }) => {
	const { selectedItemsMap, setSelectedItemsMap } = useDropdownContext();

	const [isAllSelected, setIsAllSelected] = useState(false);

	const toggleSelectAll = () => {
		if (isAllSelected) {
			// deselect all the items from the map by removing them
			setSelectedItemsMap(new Map(
				[...selectedItemsMap].filter(([key, item]) => !items.some(i => i.id === item.id))
			))
		} else {
			// select all
			setSelectedItemsMap(new Map(
				[...selectedItemsMap, ...items.map(item => [item.id, item])]
			))
		}
	}

	useEffect(() => {
		if (allItemsContainedInMap(items, selectedItemsMap)) {
			setIsAllSelected(true)
		} else {
			setIsAllSelected(false)
		}
	}, [selectedItemsMap, items])

	return (
		// select all checkbox
		<div className="group-label-wrapper focusable-item" tabIndex={0}>
			{title}
			<label>
				<input type="checkbox" hidden onChange={toggleSelectAll} />
				<p>{
					// check if all items are selected
					isAllSelected ? 'Deselect All' : 'Select All'
				}</p>
			</label>
		</div>
	)
}


// if the parent is selected, select all the children
const _DropdownItemParentSelectAll = ({ item, layerIndex, onChange }, ref) => {
	const { selectedItemsMap, setSelectedItemsMap, globalItemMap } = useDropdownContext();

	const [isInnerGroupOpen, setIsInnerGroupOpen] = useState(false);
	const [isPartialGroupOpen, setIsPartialGroupOpen] = useState(item.isOpen ?? false);
	const [hasChildren, _] = useState(item.children && item.children.length > 0);
	const [isIntermediate, setIsIntermediate] = useState();
	const childGroupRef = useRef(null);

	const isSelected = item.active && (selectedItemsMap.has(item.id) || item.hasChildren && allItemsContainedInMap(item.children, selectedItemsMap));

	useEffect(() => {
		if (hasChildren && !allItemsContainedInMap(item.children, selectedItemsMap) &&
			// check if any child is selected
			item.children.some(child => selectedItemsMap.has(child.id))) {
			setIsIntermediate(true)
		} else {
			setIsIntermediate(false)
		}
	}, [selectedItemsMap])

	const toggleGroupSelection = () => {
		setIsInnerGroupOpen(!isInnerGroupOpen)
		setIsPartialGroupOpen(false)
	}

	useEffect(() => {
		if (isSelected && hasChildren) {
			setSelectedItemsMap(new Map(
				[...selectedItemsMap, ...item.children.map(child => [child.id, child])]
			))
		}
	}, [isSelected, hasChildren])

	useEffect(() => {
		setIsPartialGroupOpen(item.isOpen)
	}, [item.isOpen])

	useEffect(() => {
		if (!item.active) {
			setSelectedItemsMap(prev => {
				const newSelectedItems = new Map(prev);
				if (newSelectedItems.has(item.id)) {
					newSelectedItems.delete(item.id);
					return newSelectedItems; // Update state only if necessary
				}
				return prev; // No change; return the previous state
			});
		}
	}, [item.active])

	useEffect(() => {
		if (ref && ref.current) {
			ref.current.element = ref.current
			ref.current.hasChildren = hasChildren
			ref.current.childGroupRef = childGroupRef

			ref.current.setIsInnerGroupOpen = setIsInnerGroupOpen
			ref.current.isInnerGroupOpen = isInnerGroupOpen

			ref.current.isSelected = isSelected
			// ref.current.setIsSelected = setIsSelected
		}
	}, [isInnerGroupOpen, setIsInnerGroupOpen, isSelected,
		// setIsSelected,
		hasChildren, item, ref, childGroupRef])
	return <>
		<div className="dropdown-item focusable-item" tabIndex={0} ref={ref ? ref : null}
			onClick={() => {
				if (!item.active)
					toggleGroupSelection()
			}}
		>
			<div className="title">
				<label disabled={!item.active}>
					<span>
						<input type="checkbox" className="mb-[1px]" onChange={(e) => {
							onChange(e, item)
						}} checked={isSelected}
							data-intermediate={isIntermediate}
							tabIndex={-1} />
						<p>{item.name}</p>
					</span>
				</label>
				{
					hasChildren &&
					<div className={`group-icon-wrapper ${isInnerGroupOpen && 'open'}`} onClick={toggleGroupSelection}>
						<span className={`group-icon`}>arrow_forward_ios</span>
					</div>
				}
			</div>
		</div>
		{
			isInnerGroupOpen &&
			hasChildren && <Group items={globalItemMap.get(item.id).children} layerIndex={(layerIndex + 1)} ref={childGroupRef} />
		}
		{
			isPartialGroupOpen && !isInnerGroupOpen &&
			hasChildren && <Group items={item.children} layerIndex={(layerIndex + 1)} ref={childGroupRef} />
		}
	</>
}

const DropdownItemParentSelectAll = React.forwardRef(_DropdownItemParentSelectAll)

const _DropdownItem = ({ item, layerIndex, onChange }, ref) => {
	const { selectedItemsMap, setSelectedItemsMap, globalItemMap } = useDropdownContext();

	const [isInnerGroupOpen, setIsInnerGroupOpen] = useState(false);
	const [isPartialGroupOpen, setIsPartialGroupOpen] = useState(item.isOpen ?? false);
	const [hasChildren, _] = useState(item.children && item.children.length > 0);
	const childGroupRef = useRef(null);

	const isSelected = item.active && selectedItemsMap.has(item.id);

	const toggleGroupSelection = () => {
		setIsInnerGroupOpen(!isInnerGroupOpen)
		setIsPartialGroupOpen(false)
	}

	useEffect(() => {
		setIsPartialGroupOpen(item.isOpen)
	}, [item.isOpen])

	useEffect(() => {
		if (!item.active) {
			setSelectedItemsMap(prev => {
				const newSelectedItems = new Map(prev);
				if (newSelectedItems.has(item.id)) {
					newSelectedItems.delete(item.id);
					return newSelectedItems; // Update state only if necessary
				}
				return prev; // No change; return the previous state
			});
		}
	}, [item.active])

	useEffect(() => {
		if (ref && ref.current) {
			ref.current.element = ref.current
			ref.current.hasChildren = hasChildren
			ref.current.childGroupRef = childGroupRef

			ref.current.setIsInnerGroupOpen = setIsInnerGroupOpen
			ref.current.isInnerGroupOpen = isInnerGroupOpen

			ref.current.isSelected = isSelected
			// ref.current.setIsSelected = setIsSelected
		}
	}, [isInnerGroupOpen, setIsInnerGroupOpen, isSelected,
		// setIsSelected,
		hasChildren, item, ref, childGroupRef])
	return <>
		<div className="dropdown-item focusable-item" tabIndex={0} ref={ref ? ref : null}
			onClick={() => {
				if (!item.active)
					toggleGroupSelection()
			}}
		>
			<div className="title">
				<label disabled={!item.active}>
					<span>
						<input type="checkbox" className="mb-[1px]" onChange={(e) => {
							onChange(e, item)
						}} checked={isSelected} tabIndex={-1} />
						<p>{item.name}</p>
					</span>
				</label>
				{
					hasChildren &&
					<div className={`group-icon-wrapper ${isInnerGroupOpen && 'open'}`} onClick={toggleGroupSelection}>
						<span className={`group-icon`}>arrow_forward_ios</span>
					</div>
				}
			</div>
		</div>
		{
			isInnerGroupOpen &&
			hasChildren && <Group items={globalItemMap.get(item.id).children} layerIndex={(layerIndex + 1)} ref={childGroupRef} />
		}
		{
			isPartialGroupOpen && !isInnerGroupOpen &&
			hasChildren && <Group items={item.children} layerIndex={(layerIndex + 1)} ref={childGroupRef} />
		}
	</>

}

const DropdownItem = React.forwardRef(_DropdownItem)

const _Input = ({ placeholder, onChange, onKeyDown }, ref) => {
	const { isDropdownOpen, setIsDropdownOpen } = useDropdownContext();

	return (
		<div className="multi-layer-dropdown-input" onKeyDown={onKeyDown} >
			<input placeholder={placeholder} autoComplete="off" onChange={onChange} ref={ref} />
			<span className={`material-symbols-rounded transition-all ${isDropdownOpen && 'rotate-180'}`} onClick={(e) => {
				e.stopPropagation();
				setIsDropdownOpen(!isDropdownOpen)
			}}>
				expand_more</span>
		</div>)
}

const Input = React.forwardRef(_Input)

const ChipContainer = () => {
	const { selectedItemsMap } = useDropdownContext();

	return (
		selectedItemsMap.size > 0 && <ChipContainerWrapper />
	)
}

const ChipContainerWrapper = () => {
	const { selectedItemsMap, setSelectedItemsMap, id } = useDropdownContext();

	const clearAll = () => {
		setSelectedItemsMap(new Map())
	}

	return <div className="multi-layer-dropdown-chip-container-wrapper" data-id={id}>
		<div className="multi-layer-dropdown-chip-container">
			{
				Array.from(selectedItemsMap).map(([key, item], index) => <Chip key={index} item={item} />)
			}
		</div>
		<span className="material-symbols-rounded clear" onClick={clearAll}>clear</span>
	</div>
}

const Chip = ({ item }) => {
	const { setSelectedItemsMap } = useDropdownContext();

	return (
		<div className="multi-layer-chip">{item.name}
			<button className="chip-close material-symbols-rounded"
				onClick={() => {
					// remove item from selectedItems
					setSelectedItemsMap(prev => {
						prev.delete(item.id)
						return new Map(prev)
					})
				}}
			>close</button>
		</div>
	)
}

export default MultiLayerDropdown;